import { useEffect, useState } from 'react';

const SESSION_KEY = 'apdn_sid';
const getSessionId = () => sessionStorage.getItem(SESSION_KEY) || 'unknown';

// Google Ad Manager fallback config
// Replace these with actual GAM network code and ad unit IDs after setup
const GAM_FALLBACKS = {
  'sidebar-top':        { adUnitId: '/XXXXXXX/apd-sidebar-top',    size: [300, 250] },
  'sidebar-mid':        { adUnitId: '/XXXXXXX/apd-sidebar-mid',    size: [300, 250] },
  'header-leaderboard': { adUnitId: '/XXXXXXX/apd-header',         size: [728, 90]  },
};

export default function AdSlot({ slotId, className = '' }) {
  const [ad, setAd] = useState(null);       // null = loading, false = no direct ad
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`/.netlify/functions/get-active-ads?slots=${slotId}`)
      .then(r => r.json())
      .then(data => {
        setAd(data[slotId] || false);
        setLoading(false);
      })
      .catch(() => { setAd(false); setLoading(false); });
  }, [slotId]);

  // Track impression once ad is resolved
  useEffect(() => {
    if (loading || ad === null) return;
    if (ad && ad.campaign_id) {
      fetch('/.netlify/functions/track-ad-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'impression',
          campaignId: ad.campaign_id,
          slotId,
          sessionId: getSessionId(),
          path: window.location.pathname,
        }),
      }).catch(() => {});
    }
  }, [ad, loading, slotId]);

  const handleClick = () => {
    if (!ad?.campaign_id) return;
    fetch('/.netlify/functions/track-ad-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'click',
        campaignId: ad.campaign_id,
        slotId,
        sessionId: getSessionId(),
        path: window.location.pathname,
      }),
    }).catch(() => {});
  };

  if (loading) return <div className={`min-h-[50px] animate-pulse bg-white/5 rounded-lg ${className}`} />;

  // Direct-sold banner ad
  if (ad && ad.slot_type === 'banner') {
    return (
      <div className={`rounded-lg overflow-hidden ${className}`}>
        <a href={ad.destination_url} target="_blank" rel="noopener sponsored" onClick={handleClick}>
          <img src={ad.image_url} alt={ad.headline || 'Advertisement'} className="w-full block" />
        </a>
        <div className="text-[10px] text-gray-500 text-right mt-1">Sponsored</div>
      </div>
    );
  }

  // Direct-sold directory spotlight
  if (ad && ad.slot_type === 'directory_spotlight') {
    return (
      <div className={`border border-white/10 rounded-lg p-4 bg-white/5 ${className}`}>
        <div className="flex items-center gap-3">
          {ad.logo_url && (
            <img src={ad.logo_url} alt={ad.headline} className="w-12 h-12 object-contain flex-shrink-0" />
          )}
          <div className="min-w-0">
            <div className="text-[11px] text-purple-400 font-semibold uppercase tracking-wider">
              Featured Provider
            </div>
            <a
              href={ad.destination_url}
              target="_blank"
              rel="noopener sponsored"
              onClick={handleClick}
              className="font-bold text-blue-400 hover:text-blue-300 no-underline block truncate"
            >
              {ad.headline}
            </a>
            {ad.body_text && (
              <p className="text-sm text-gray-300 mt-1 line-clamp-2">{ad.body_text}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Direct-sold sponsored content
  if (ad && ad.slot_type === 'sponsored_content') {
    return (
      <div className={`bg-amber-500/10 border border-amber-400/30 rounded-lg p-5 ${className}`}>
        <div className="text-[11px] font-bold text-amber-400 uppercase tracking-wider mb-2">
          Sponsored Resource
        </div>
        <a
          href={ad.destination_url}
          target="_blank"
          rel="noopener sponsored"
          onClick={handleClick}
          className="font-bold text-lg text-blue-400 hover:text-blue-300 no-underline block mb-1"
        >
          {ad.headline}
        </a>
        {ad.body_text && (
          <p className="text-sm text-gray-300 leading-relaxed">{ad.body_text}</p>
        )}
      </div>
    );
  }

  // GAM / AdSense fallback — only render if GAM is actually configured
  const gam = GAM_FALLBACKS[slotId];
  if (gam && !gam.adUnitId.includes('XXXXXXX') && typeof window.googletag !== 'undefined') {
    return (
      <div className={`${className}`}>
        <div
          id={`gam-${slotId}`}
          className="mx-auto"
          style={{ width: gam.size[0], height: gam.size[1], maxWidth: '100%' }}
        />
      </div>
    );
  }

  return null; // No ad, no fallback — render nothing, don't shift layout
}
